import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../store/authStore'
import {
  getConsent,
  hasDecided,
  onOpenCookieSettings,
  openCookieSettings,
  setConsent,
} from '../lib/consent'

// KVKK + GDPR cookie consent — banner + granular preferences + withdrawal.
//
// Deliberately NOT wired through i18next: like the marketing/legal pages
// (LandingPage, Privacy, Terms) this is standalone legal copy maintained in
// TR + EN only, chosen from the active UI language. Turkish UI → Turkish text;
// every other language → English. Keep both columns in sync when editing.

type Lang = 'tr' | 'en'
type Mode = 'hidden' | 'banner' | 'prefs'

const COPY: Record<Lang, {
  privacyPath: string
  banner: { title: string; body: string; learnMore: string }
  buttons: { acceptAll: string; rejectAll: string; manage: string; save: string; back: string }
  prefs: {
    title: string
    intro: string
    necessary: { name: string; desc: string; always: string }
    analytics: { name: string; desc: string }
    marketing: { name: string; desc: string }
  }
  reopen: string
}> = {
  tr: {
    privacyPath: '/gizlilik-politikasi',
    banner: {
      title: 'Çerezler ve gizliliğiniz',
      body:
        'Oturumunuzu açık tutmak için gerekli çerezleri kullanıyoruz. İzniniz olursa, hizmeti geliştirmek amacıyla anonim kullanım istatistikleri (PostHog, AB sunucuları) ve pazarlama çerezleri de kullanmak istiyoruz. Tercihinizi dilediğiniz zaman değiştirebilirsiniz.',
      learnMore: 'Gizlilik Politikası',
    },
    buttons: {
      acceptAll: 'Tümünü kabul et',
      rejectAll: 'Yalnızca gerekli',
      manage: 'Tercihleri yönet',
      save: 'Seçimimi kaydet',
      back: 'Geri',
    },
    prefs: {
      title: 'Çerez tercihleri',
      intro:
        'Her kategoriyi ayrı ayrı yönetin. Gerekli çerezler hizmetin çalışması için zorunludur ve kapatılamaz.',
      necessary: {
        name: 'Zorunlu',
        desc: 'Oturum açma, güvenlik ve dil tercihi. Bunlar olmadan platform çalışmaz.',
        always: 'Her zaman açık',
      },
      analytics: {
        name: 'Analitik',
        desc: 'Hangi özelliklerin nasıl kullanıldığını anlamamıza yardımcı olan anonim istatistikler (PostHog, AB). Reklam için kullanılmaz.',
      },
      marketing: {
        name: 'Pazarlama',
        desc: 'Kampanya performansını ölçmek ve size uygun içerik göstermek için kullanılan çerezler.',
      },
    },
    reopen: 'Çerez ayarları',
  },
  en: {
    privacyPath: '/privacy',
    banner: {
      title: 'Cookies & your privacy',
      body:
        'We use strictly necessary cookies to keep you signed in. With your permission, we also use anonymous usage analytics (PostHog, EU-hosted) to improve the product and marketing cookies. You can change your choice at any time.',
      learnMore: 'Privacy Policy',
    },
    buttons: {
      acceptAll: 'Accept all',
      rejectAll: 'Necessary only',
      manage: 'Manage preferences',
      save: 'Save my choice',
      back: 'Back',
    },
    prefs: {
      title: 'Cookie preferences',
      intro:
        'Manage each category independently. Necessary cookies are required for the service to work and cannot be turned off.',
      necessary: {
        name: 'Strictly necessary',
        desc: 'Sign-in, security and language preference. The platform will not work without them.',
        always: 'Always on',
      },
      analytics: {
        name: 'Analytics',
        desc: 'Anonymous statistics (PostHog, EU) that help us understand which features are used and how. Never used for advertising.',
      },
      marketing: {
        name: 'Marketing',
        desc: 'Cookies used to measure campaign performance and show you relevant content.',
      },
    },
    reopen: 'Cookie settings',
  },
}

function Toggle({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean
  disabled?: boolean
  onChange?: (v: boolean) => void
  label: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange?.(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
        checked ? 'bg-brand-600' : 'bg-gray-300'
      } ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  )
}

export default function CookieConsent() {
  const { i18n } = useTranslation()
  const lang: Lang = i18n.language?.toLowerCase().startsWith('tr') ? 'tr' : 'en'
  const c = COPY[lang]
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  const [mode, setMode] = useState<Mode>('hidden')
  const [analytics, setAnalytics] = useState(false)
  const [marketing, setMarketing] = useState(false)

  // Decide initial mode after mount (localStorage isn't available during SSR-safe render).
  useEffect(() => {
    if (!hasDecided()) setMode('banner')
  }, [])

  // Let external triggers (floating button, footer link) reopen the panel.
  useEffect(() => {
    return onOpenCookieSettings(() => {
      const current = getConsent()
      setAnalytics(current.analytics)
      setMarketing(current.marketing)
      setMode('prefs')
    })
  }, [])

  function openPrefs() {
    const current = getConsent()
    setAnalytics(current.analytics)
    setMarketing(current.marketing)
    setMode('prefs')
  }

  function acceptAll() {
    setConsent({ analytics: true, marketing: true })
    setMode('hidden')
  }

  function rejectAll() {
    setConsent({ analytics: false, marketing: false })
    setMode('hidden')
  }

  function saveCustom() {
    setConsent({ analytics, marketing })
    setMode('hidden')
  }

  // Discreet reopener so withdrawal is always one click away (GDPR/KVKK: as easy
  // to withdraw as to grant). Only shown once a decision exists and no panel is open.
  // Hidden inside the authenticated portal — logged-in users don't want a floating
  // cookie button hovering over the app; withdrawal stays available on the public /
  // marketing + legal pages (footer / openCookieSettings). The panel can still be
  // opened programmatically via the openCookieSettings event if we ever add a link.
  if (mode === 'hidden') {
    if (!hasDecided() || isAuthenticated) return null
    return (
      <button
        type="button"
        onClick={openCookieSettings}
        aria-label={c.reopen}
        title={c.reopen}
        className="fixed bottom-4 left-4 z-40 flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white/90 text-lg shadow-md backdrop-blur hover:bg-white"
      >
        <span aria-hidden>🍪</span>
      </button>
    )
  }

  if (mode === 'banner') {
    return (
      <div
        role="dialog"
        aria-modal="false"
        aria-label={c.banner.title}
        className="fixed inset-x-0 bottom-0 z-50 p-3 sm:p-4"
      >
        <div className="mx-auto max-w-3xl rounded-xl border border-gray-200 bg-white p-4 shadow-xl sm:p-5">
          <h2 className="text-sm font-semibold text-gray-900">{c.banner.title}</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-gray-600">
            {c.banner.body}{' '}
            <a
              href={c.privacyPath}
              className="font-medium text-brand-600 underline hover:text-brand-700"
            >
              {c.banner.learnMore}
            </a>
          </p>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
            <button
              type="button"
              onClick={openPrefs}
              className="order-3 rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 sm:order-1 sm:mr-auto"
            >
              {c.buttons.manage}
            </button>
            <button
              type="button"
              onClick={rejectAll}
              className="order-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {c.buttons.rejectAll}
            </button>
            <button
              type="button"
              onClick={acceptAll}
              className="order-1 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 sm:order-3"
            >
              {c.buttons.acceptAll}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // prefs
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={c.prefs.title}
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-3 sm:items-center sm:p-4"
    >
      <div className="w-full max-w-lg rounded-xl border border-gray-200 bg-white p-5 shadow-xl">
        <h2 className="text-base font-semibold text-gray-900">{c.prefs.title}</h2>
        <p className="mt-1 text-sm text-gray-600">{c.prefs.intro}</p>

        <div className="mt-4 space-y-3">
          <div className="flex items-start justify-between gap-4 rounded-lg border border-gray-100 bg-gray-50 p-3">
            <div>
              <div className="text-sm font-medium text-gray-900">{c.prefs.necessary.name}</div>
              <div className="mt-0.5 text-xs leading-relaxed text-gray-500">{c.prefs.necessary.desc}</div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Toggle checked disabled label={c.prefs.necessary.name} />
              <span className="text-[10px] uppercase tracking-wide text-gray-400">
                {c.prefs.necessary.always}
              </span>
            </div>
          </div>

          <div className="flex items-start justify-between gap-4 rounded-lg border border-gray-100 p-3">
            <div>
              <div className="text-sm font-medium text-gray-900">{c.prefs.analytics.name}</div>
              <div className="mt-0.5 text-xs leading-relaxed text-gray-500">{c.prefs.analytics.desc}</div>
            </div>
            <Toggle checked={analytics} onChange={setAnalytics} label={c.prefs.analytics.name} />
          </div>

          <div className="flex items-start justify-between gap-4 rounded-lg border border-gray-100 p-3">
            <div>
              <div className="text-sm font-medium text-gray-900">{c.prefs.marketing.name}</div>
              <div className="mt-0.5 text-xs leading-relaxed text-gray-500">{c.prefs.marketing.desc}</div>
            </div>
            <Toggle checked={marketing} onChange={setMarketing} label={c.prefs.marketing.name} />
          </div>
        </div>

        <div className="mt-5 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setMode(hasDecided() ? 'hidden' : 'banner')}
            className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            {c.buttons.back}
          </button>
          <button
            type="button"
            onClick={saveCustom}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
          >
            {c.buttons.save}
          </button>
        </div>
      </div>
    </div>
  )
}
